import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listCustomers, createCustomer, updateCustomer, deleteCustomer,
} from '../services/api';
import ClientFields, { EMPTY_CLIENT as EMPTY, clientPayload } from '../components/ClientFields';

// One list of clients. Whether a client is billed or visited is decided in
// each report or appointment; the filter only narrows the list by that use.
const FILTERS = [
  { key: 'all', keep: () => true },
  { key: 'billing', keep: (row) => row.billing_uses > 0 },
  { key: 'intervention', keep: (row) => row.intervention_uses > 0 },
];

export default function ClientsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null); // null | { row } (row null = new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    listCustomers().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({ row: null }); setError(''); };
  const openEdit = (row) => { setForm({ ...EMPTY, ...row }); setEditing({ row }); setError(''); };
  const closeForm = () => { setEditing(null); setForm(EMPTY); setError(''); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(t('clients.nameRequired')); return; }
    setSaving(true); setError('');
    try {
      const body = clientPayload(form);
      if (editing.row) await updateCustomer(editing.row.id, body);
      else await createCustomer(body);
      closeForm();
      load();
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(t('clients.deleteConfirmOne'))) return;
    try {
      await deleteCustomer(row.id);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || t('errors.generic'));
    }
  };

  const shown = rows.filter(FILTERS.find((f) => f.key === filter).keep);

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-title">{t('clients.title')}</h1>
        <button className="btn btn-primary" onClick={openNew}>+ {t('clients.add')}</button>
      </div>

      <div className="client-filter" role="radiogroup" aria-label={t('clients.filterLabel')}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="radio"
            aria-checked={filter === f.key}
            className={`client-filter-chip${filter === f.key ? ' is-on' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {t(`clients.filter_${f.key}`)}
            <span className="client-filter-count">{rows.filter(f.keep).length}</span>
          </button>
        ))}
      </div>

      {editing && (
        <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--color-primary)' }}>
          <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
            {editing.row ? t('clients.editTitleOne') : t('clients.newTitleOne')}
          </h3>
          <form onSubmit={handleSave}>
            <ClientFields value={form} onChange={setForm} />
            {error && <p style={{ color: 'var(--color-danger)', fontSize: 13, fontWeight: 500, marginTop: 12 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? t('auth.loading') : t('clients.save')}
              </button>
              <button className="btn btn-secondary" type="button" onClick={closeForm}>{t('clients.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', paddingTop: 20 }}>{t('auth.loading')}</p>
      ) : shown.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-icon">&#128100;</div>
          <p>{rows.length === 0 ? t('clients.emptyOne') : t(`clients.emptyFilter_${filter}`)}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {shown.map((row) => (
            <div className="card client-card" key={row.id} style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {row.name}
                    {row.kind === 'individual' && (
                      <span className="badge badge-individual" style={{ marginLeft: 8, fontSize: 10 }}>
                        {t('clients.individualTag')}
                      </span>
                    )}
                    {row.invoiceninja_client_id && (
                      <span className="badge badge-completed" style={{ marginLeft: 8, fontSize: 10 }}>
                        {t('clients.linkedToIN')}
                      </span>
                    )}
                  </div>
                  <div className="client-uses">
                    {row.billing_uses > 0 && (
                      <span className="client-use client-use-billing">{t('clients.usesBilling', { count: row.billing_uses })}</span>
                    )}
                    {row.intervention_uses > 0 && (
                      <span className="client-use">{t('clients.usesIntervention', { count: row.intervention_uses })}</span>
                    )}
                    {!row.billing_uses && !row.intervention_uses && (
                      <span className="client-use client-use-none">{t('clients.usesNone')}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-light)', marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {row.vat_number && <span>{t('clients.vatShort')}: {row.vat_number}</span>}
                    {(row.postal_code || row.city) && <span>{[row.postal_code, row.city].filter(Boolean).join(' ')}</span>}
                    {(row.billing_email || row.email) && <span>{row.billing_email || row.email}</span>}
                    {row.contact_person && <span>{row.contact_person}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(row)}>{t('clients.edit')}</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(row)}>{t('clients.delete')}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
